import { FC, ReactNode } from 'react';
import styled from 'styled-components';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';

import { NgTooltip } from '../..';

const StyledLayoutValue = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  width: 100%;
  min-width: 0;
`;

const StyledLayoutText = styled.span`
  flex: 1;
  width: 0;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledLayoutSupportIcon = styled.span<{ $status: SlideLayoutSupportIndicator | 'UNKNOWN' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme, $status }) => {
    switch ($status) {
      case 'FULLY_SUPPORTED':
        return theme.palette.success;
      case 'PARTIALLY_SUPPORTED':
        return theme.palette.warning;
      case 'NOT_SUPPORTED':
        return theme.palette.error;
      default:
        return theme.palette.error;
    }
  }};
`;

interface LayoutCompatibilityBadgeProps {
  layoutName: string | null | undefined;
  supportLevel: SlideLayoutSupportIndicator | undefined | null;
  successTooltipTitle?: ReactNode;
  errorTooltipTitle?: ReactNode;
  warningTooltipTitle?: ReactNode;
  hideLabel?: boolean;
  tooltipZIndex?: number;
}

export const LayoutCompatibilityBadge: FC<LayoutCompatibilityBadgeProps> = ({
  layoutName,
  supportLevel,
  successTooltipTitle,
  errorTooltipTitle,
  warningTooltipTitle,
  hideLabel = false,
  tooltipZIndex,
}) => {
  const getStatusConfig = (status: SlideLayoutSupportIndicator | null | undefined) => {
    switch (status) {
      case 'FULLY_SUPPORTED':
        return {
          status: 'FULLY_SUPPORTED' as const,
          icon: <CheckCircleIcon fontSize="small" />,
          tooltipTitle: successTooltipTitle,
        };
      case 'PARTIALLY_SUPPORTED':
        return {
          status: 'PARTIALLY_SUPPORTED' as const,
          icon: <InfoIcon fontSize="small" />,
          tooltipTitle: warningTooltipTitle,
        };
      case 'NOT_SUPPORTED':
        return {
          status: 'NOT_SUPPORTED' as const,
          icon: <ErrorIcon fontSize="small" />,
          tooltipTitle: errorTooltipTitle,
        };
      default:
        return null;
    }
  };

  const statusConfig = getStatusConfig(supportLevel);

  if (!statusConfig) {
    return (
      <StyledLayoutValue>
        {!hideLabel && <StyledLayoutText title={layoutName || '-'}>{`Layout: ${layoutName || '-'}`}</StyledLayoutText>}
      </StyledLayoutValue>
    );
  }

  return (
    <StyledLayoutValue>
      {!hideLabel && <StyledLayoutText title={layoutName || '-'}>{`Layout: ${layoutName || '-'}`}</StyledLayoutText>}
      {statusConfig.tooltipTitle ? (
        <NgTooltip title={statusConfig.tooltipTitle} placement="top" zIndex={tooltipZIndex}>
          <StyledLayoutSupportIcon $status={statusConfig.status}>{statusConfig.icon}</StyledLayoutSupportIcon>
        </NgTooltip>
      ) : (
        <StyledLayoutSupportIcon $status={statusConfig.status}>{statusConfig.icon}</StyledLayoutSupportIcon>
      )}
    </StyledLayoutValue>
  );
};
